import React from 'react';
import { ArrowLeft, Github, ExternalLink, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Carousel } from 'react-responsive-carousel';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import RecipeGeneratorAI from "@/assets/achivements/RecipeGeneratorAI.png";
import chefgpt_ui from '@/assets/achivements/chefgpt-ui.png';
import portalrg from '@/assets/achivements/portal-chef-gpt.png';
const Recipe_Generator_AI: React.FC = () => {
  return (
    <main className="min-h-screen bg-background pt-20">
      {/* Back Navigation */}
      <div className="max-w-6xl mx-auto px-4 pt-16 md:pt-20">
        <Link to="/" className="flex items-center text-muted-foreground hover:text-primary transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Portfolio
        </Link>
      </div>
      
      {/* Hero Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col gap-4">
            <Badge variant="secondary" className="w-fit">Featured Project</Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Recipe Generator AI</h1>
            
            <div className="flex flex-wrap gap-2 my-4">
              <Badge variant="outline" className="bg-white/5 border-primary/20">Flask</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">PyTorch</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">T5 Transformers</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Azure App Service</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">Quantized Model</Badge>
              <Badge variant="outline" className="bg-white/5 border-primary/20">REST API</Badge>
            </div>
          </div>
        </div>
      </section>
      
      {/* Project Image */}
      <section className="py-6 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="w-full overflow-hidden rounded-xl border border-border shadow-sm">
            <Carousel
              showThumbs={false}
              showStatus={false}
              infiniteLoop
              autoPlay
              interval={5000}
              className="project-carousel"
            >
              <div>
                <img src={chefgpt_ui} alt="Recipe Generator AI - Overview" className="w-full object-cover" />
              </div>
              <div>
              <img src={portalrg} alt="AI Recipe Generation" className="w-full object-cover" />
              </div>
              <div>
              <img src={RecipeGeneratorAI} alt="AI Recipe Generation" className="w-full object-cover" />
              </div>
            </Carousel>
          </div>
        </div>
      </section>
      
      {/* Project Details */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="md:col-span-2 space-y-6">
              <div className="bg-card p-8 rounded-xl border border-border shadow-sm">
                <h2 className="text-2xl font-bold mb-4 text-foreground">Project Overview</h2>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  --------------- ChefGPT Recipe Generator is a Flask-based web service that generates cooking recipes using a quantized T5 model, 
                  optimized for deployment on Azure App Service Basic B1. The system provides efficient recipe generation with low CPU usage, 
                  making it perfect for cost-effective cloud deployment while maintaining high-quality AI-generated recipes.
                </p>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">Core Features</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- Low CPU Usage</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Optimized for Azure App Service Basic B1 with CPU-only PyTorch, ensuring efficient resource utilization 
                      and cost-effective deployment without compromising performance.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">---- Quantized Model</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Uses dynamic quantization to reduce memory footprint while maintaining high-quality recipe generation. 
                      The model is optimized for production deployment with minimal resource requirements.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">---- Efficient Processing</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Thread-safe model loading and inference with comprehensive error handling and logging. 
                      Built-in health monitoring and automatic garbage collection for optimal performance.
                    </p>
                  </div>
                </div>
                
                <h3 className="text-xl font-semibold mb-3 text-foreground">API Endpoints</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">POST /generate_recipe</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Generate a recipe from a text prompt with customizable parameters including max_length and temperature 
                      for controlling recipe creativity and length.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">GET /health</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Health check endpoint providing service status, model loading status, and timestamp for monitoring 
                      and debugging purposes.
                    </p>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">GET /</h4>
                    <p className="text-muted-foreground leading-relaxed">
                      Service information endpoint displaying available endpoints and API documentation for easy integration.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Performance Optimization</h3>
                <div className="space-y-4 mb-6">
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- CPU-Only PyTorch</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Uses CPU-optimized PyTorch builds for Azure App Service</li>
                      <li>No GPU dependencies for cost-effective deployment</li>
                      <li>Optimized for Basic B1 tier resource constraints</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- Dynamic Quantization</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Reduces model size and inference time</li>
                      <li>Maintains quality while minimizing memory usage</li>
                      <li>Efficient model loading and processing</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-foreground mb-2">--- Production Ready</h4>
                    <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
                      <li>Thread-safe model loading and inference</li>
                      <li>Comprehensive error handling and logging</li>
                      <li>Built-in health monitoring and status checks</li>
                      <li>Automatic garbage collection and memory management</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Technical Stack</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">F</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Flask</p>
                      <p className="text-sm text-muted-foreground">Web framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">P</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">PyTorch</p>
                      <p className="text-sm text-muted-foreground">ML framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">T</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">T5 Transformers</p>
                      <p className="text-sm text-muted-foreground">Text generation</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold">A</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Azure</p>
                      <p className="text-sm text-muted-foreground">Cloud platform</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Project Outcome</h3>
                <p className="text-muted-foreground">
                  The system successfully generates coherent recipes and creates precise grocery lists, 
                  significantly improving the cooking experience and reducing food waste through better planning.
                </p>
              </div>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Project Links</h3>
                <div className="space-y-3">
                  <Button asChild className="w-full" variant="outline">
                    <a href="https://github.com/Mohan-Balaji/ChefGPT-RecipeGen" target="_blank" rel="noopener noreferrer">
                      <Github className="h-4 w-4 mr-2" />
                      View Code
                    </a>
                  </Button>
                  <Button asChild className="w-full" variant="outline">
                    <a href="https://recipe-generator-ai-fyg0a0csadb3ehaw.centralindia-01.azurewebsites.net/" target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Live Demo
                    </a>
                  </Button>
                </div>
              </div>
              
              <div className="bg-card p-6 rounded-xl border border-border shadow-sm">
                <h3 className="text-lg font-semibold mb-4 text-foreground">Technologies Used</h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">F</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Flask</p>
                      <p className="text-sm text-muted-foreground">Web framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">P</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">PyTorch</p>
                      <p className="text-sm text-muted-foreground">ML framework</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">T</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">T5 Transformers</p>
                      <p className="text-sm text-muted-foreground">Text generation</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">A</span>
                    </div>
                    <div>
                      <p className="font-medium text-foreground">Azure</p>
                      <p className="text-sm text-muted-foreground">Cloud platform</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Related Projects */}
      <section className="py-12 bg-muted/5">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-foreground">Other Projects You Might Like</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/Book_Recommendation_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Book Recommendation System</h3>
              <p className="text-muted-foreground line-clamp-2">An intelligent book recommendation system using machine learning algorithms.</p>
            </Link>
            
            <Link to="/Inventory_Management_System" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Inventory Management System</h3>
              <p className="text-muted-foreground line-clamp-2">A comprehensive system for managing inventory and tracking stock levels.</p>
            </Link>
            
            <Link to="/Homelab_Cloud_Storage" className="bg-card p-6 rounded-xl border border-border shadow-sm hover:border-primary/20 transition-all hover:-translate-y-0.5 duration-300">
              <h3 className="text-lg font-semibold text-foreground mb-2">Homelab Cloud Storage</h3>
              <p className="text-muted-foreground line-clamp-2">A self-hosted cloud storage solution using TrueNAS on a repurposed machine.</p>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Recipe_Generator_AI;

